import type { BuildCalendarLink } from '../types/buildTeam';
import type { EventCalendarReverseLink } from '../types/eventTeam';
import type { Partnership } from '../types/partnership';
import { buildTeamApiService } from './buildTeamApi';
import { eventTeamApiService } from './eventTeamApi';
import { partnershipsApiService } from './partnershipsApi';

/**
 * Which team modules can link a calendar event. Each has a reverse-lookup
 * endpoint (`…/calendar/event/{calendarEventId}`) that lists its records
 * pointing at the event; the calendar uses it to draw linked events quietly.
 */
export type CalendarLinkSource = 'events' | 'builds' | 'partnerships';

export const CALENDAR_LINK_SOURCES: readonly CalendarLinkSource[] = ['events', 'builds', 'partnerships'];

export const CALENDAR_LINK_SOURCE_LABELS: Record<CalendarLinkSource, string> = {
  events: 'Event Team',
  builds: 'Build Team',
  partnerships: 'Partnerships',
};

/** One record in another module that links to a calendar event. */
export interface CalendarLinkTarget {
  source: CalendarLinkSource;
  /** e.g. "Event", "Event project", "Build", "Partnership". */
  kind: string;
  /** The record's title where the API returns one, otherwise its kind. */
  label: string;
  /** In-app route to the record. */
  href: string;
}

export interface CalendarLinkSummary {
  eventId: string;
  targets: CalendarLinkTarget[];
}

export type CalendarLinkFetcher = (calendarEventId: string) => Promise<CalendarLinkTarget[]>;

export interface CalendarLinkResolver {
  /** Look an event up in every requested source, using the session cache where possible. */
  resolve(eventId: string, sources: readonly CalendarLinkSource[]): Promise<CalendarLinkSummary>;
  /** The cached summary, or undefined while any requested source is still unknown. */
  peek(eventId: string, sources: readonly CalendarLinkSource[]): CalendarLinkSummary | undefined;
  /** True once a source has answered 401/403 and is skipped for the session. */
  isUnavailable(source: CalendarLinkSource): boolean;
}

// ── Response → target mapping ────────────────────────────────────────────────

export function toEventTeamTargets(rows: readonly EventCalendarReverseLink[]): CalendarLinkTarget[] {
  return rows.map(row => {
    const isEvent = row.target_type === 'event';
    const kind = isEvent ? 'Event' : 'Event project';
    return {
      source: 'events',
      kind,
      label: row.title?.trim() || kind,
      href: isEvent ? `/event-team/events/${row.target_id}` : `/event-team/projects/${row.target_id}`,
    };
  });
}

export function toBuildTeamTargets(rows: readonly BuildCalendarLink[]): CalendarLinkTarget[] {
  return rows.map(row => {
    const isProject = row.target_type === 'project';
    const kind = isProject ? 'Build project' : 'Build';
    return {
      source: 'builds',
      kind,
      label: kind,
      href: isProject ? `/build-team/projects/${row.target_id}` : `/build-team/builds/${row.target_id}`,
    };
  });
}

export function toPartnershipTargets(rows: readonly Partnership[]): CalendarLinkTarget[] {
  return rows.map(row => ({
    source: 'partnerships',
    kind: 'Partnership',
    label: row.summary?.trim() || 'Partnership',
    href: `/partnerships/${row.id}`,
  }));
}

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const defaultFetchers: Record<CalendarLinkSource, CalendarLinkFetcher> = {
  events: async id => toEventTeamTargets(asArray(await eventTeamApiService.getTargetsForCalendarEvent(id))),
  builds: async id => toBuildTeamTargets(asArray(await buildTeamApiService.getBuildsAndProjectsForEvent(id))),
  partnerships: async id =>
    toPartnershipTargets(asArray(await partnershipsApiService.getPartnershipsByCalendarEvent(id))),
};

// ── Resolver ─────────────────────────────────────────────────────────────────

/**
 * The HTTP status behind a failed lookup. The event-team client throws an
 * error carrying `status`; the others embed it in "API Error: 403 …".
 */
export function errorStatus(error: unknown): number | null {
  if (error && typeof error === 'object' && typeof (error as { status?: unknown }).status === 'number') {
    return (error as { status: number }).status;
  }
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const match = /API Error: (\d{3})/.exec(message);
  return match ? Number(match[1]) : null;
}

/** Run at most `max` tasks at once; the rest wait their turn in order. */
function createLimiter(max: number) {
  let active = 0;
  const waiting: (() => void)[] = [];
  return async <T>(task: () => Promise<T>): Promise<T> => {
    if (active >= max) await new Promise<void>(resolve => waiting.push(resolve));
    active++;
    try {
      return await task();
    } finally {
      active--;
      waiting.shift()?.();
    }
  };
}

export interface CalendarLinkResolverOptions {
  /** Requests in flight at once across every source. */
  concurrency?: number;
}

/**
 * Reverse-lookup cache for the session. Every (event, source) pair is fetched
 * at most once; a 401/403 turns the source off for the rest of the session so
 * users without that module never spam it; a 404 is an empty result; any other
 * failure is treated as empty for now and retried on the next request.
 */
export function createCalendarLinkResolver(
  fetchers: Record<CalendarLinkSource, CalendarLinkFetcher>,
  options: CalendarLinkResolverOptions = {},
): CalendarLinkResolver {
  const limit = createLimiter(Math.max(1, options.concurrency ?? 6));
  const cache = new Map<string, Partial<Record<CalendarLinkSource, CalendarLinkTarget[]>>>();
  const inflight = new Map<string, Promise<CalendarLinkTarget[]>>();
  const unavailable = new Set<CalendarLinkSource>();

  const cached = (eventId: string, source: CalendarLinkSource) => cache.get(eventId)?.[source];

  const store = (eventId: string, source: CalendarLinkSource, targets: CalendarLinkTarget[]) => {
    const entry = cache.get(eventId) ?? {};
    entry[source] = targets;
    cache.set(eventId, entry);
  };

  const fetchSource = (eventId: string, source: CalendarLinkSource): Promise<CalendarLinkTarget[]> => {
    const key = `${eventId} ${source}`;
    const pending = inflight.get(key);
    if (pending) return pending;

    const run = limit(async () => {
      if (unavailable.has(source)) return [];
      try {
        const targets = await fetchers[source](eventId);
        store(eventId, source, targets);
        return targets;
      } catch (error) {
        const status = errorStatus(error);
        if (status === 401 || status === 403) unavailable.add(source);
        else if (status === 404) store(eventId, source, []);
        return [];
      }
    }).finally(() => {
      inflight.delete(key);
    });

    inflight.set(key, run);
    return run;
  };

  const summarise = (eventId: string, parts: (CalendarLinkTarget[] | undefined)[]): CalendarLinkSummary => ({
    eventId,
    targets: parts.flatMap(part => part ?? []),
  });

  return {
    async resolve(eventId, sources) {
      const parts = await Promise.all(
        sources.map(source => {
          if (unavailable.has(source)) return Promise.resolve<CalendarLinkTarget[]>([]);
          const hit = cached(eventId, source);
          return hit ? Promise.resolve(hit) : fetchSource(eventId, source);
        }),
      );
      return summarise(eventId, parts);
    },

    peek(eventId, sources) {
      const parts: (CalendarLinkTarget[] | undefined)[] = [];
      for (const source of sources) {
        if (unavailable.has(source)) continue;
        const hit = cached(eventId, source);
        if (!hit) return undefined;
        parts.push(hit);
      }
      return summarise(eventId, parts);
    },

    isUnavailable(source) {
      return unavailable.has(source);
    },
  };
}

/** The app-wide resolver, shared by the calendar views and the detail modal. */
export const calendarLinkResolver = createCalendarLinkResolver(defaultFetchers);
